import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import MaskedPhone from '../components/MaskedPhone';
import { countBrokerListings } from '../lib/brokerReviews';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BrokerRatingSummary from '../components/BrokerRatingSummary';
import LeaveReviewForm from '../components/LeaveReviewForm';
import ReviewsList from '../components/ReviewsList';
import BrokerAvatarUpload from '../components/BrokerAvatarUpload';
import { toLanguageArray } from '../lib/languages';
import './BrokerProfilePage.css';

const IcCheck  = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>);
const IcPin    = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>);
const IcPhone  = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.7 12 19.79 19.79 0 0 1 1.6 3.41 2 2 0 0 1 3.56 1.5h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.06a16 16 0 0 0 6.03 6.03l1.72-1.72a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>);
const IcMail   = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>);
const IcGlobe  = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>);
const IcEdit   = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const IcClose  = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>);
const IcHome   = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>);
const IcStar   = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>);

function fmt(n) {
  if (!n) return '';
  if (n >= 1000000) return '€' + (n/1000000).toFixed(1) + 'M';
  if (n >= 1000)    return '€' + Math.round(n/1000) + 'K';
  return '€' + n;
}

export default function BrokerProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  const [broker,       setBroker]       = useState(null);
  const [listings,     setListings]     = useState([]);
  const [listingCount, setListingCount] = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [reviewRefresh, setReviewRefresh] = useState(0);

  const [showEdit,    setShowEdit]    = useState(false);
  const [editForm,    setEditForm]    = useState({});
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [showContact, setShowContact] = useState(false);

  useEffect(() => {
    supabase.from('brokers').select('*').eq('id', id).single().then(({ data }) => {
      setBroker(data);
      if (data?.email) {
        supabase.from('listings').select('*').eq('contact_email', data.email)
          .not('status', 'in', '(archived,deleted)')
          .then(({ data: lData }) => setListings(lData || []));
        countBrokerListings(data.email).then(setListingCount);
      }
      setLoading(false);
    });
  }, [id]);

  const isOwner = !!(user && broker && user.email === broker.email);

  const openEdit = () => {
    setEditForm({
      phone:       broker.phone       || '',
      description: broker.description || broker.bio || '',
      speciality:  broker.speciality  || '',
      languages:   broker.languages   || '',
    });
    setShowEdit(true);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase.from('brokers').update({
      phone:       editForm.phone,
      description: editForm.description,
      speciality:  editForm.speciality,
      languages:   editForm.languages,
    }).eq('id', broker.id);

    if (!error) {
      setBroker(b => ({ ...b, ...editForm }));
      setSaved(true);
      setTimeout(() => { setShowEdit(false); setSaved(false); }, 1200);
    }
    setSaving(false);
  };

  const handleReviewSubmitted = useCallback(() => {
    setReviewRefresh(n => n + 1);
  }, []);

  if (loading) {
    return (
      <div className="bpp-loading">
        <div className="bpp-spinner" />
      </div>
    );
  }

  if (!broker) {
    return (
      <div className="bpp-not-found">
        <p>{t('brokerProfile.notFound')}</p>
        <button onClick={() => navigate(-1)}>{t('brokerProfile.back')}</button>
      </div>
    );
  }

  const initial      = (broker.name || 'B').charAt(0).toUpperCase();
  const specialities = (broker.speciality || '').split(',').map(s => s.trim()).filter(Boolean);
  const langs        = toLanguageArray(broker.languages);
  const bio          = broker.description || broker.bio || '';
  const photo        = broker.avatar_url || broker.agency_logo_url;

  return (
    <div className="agentProfile">
      <Navbar />

      <div className="agentProfile__container">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="agentProfile__back"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: 'inherit' }}
        >
          ← {t('brokerProfile.back')}
        </button>

        {/* === HEADER === */}
        <header className="agentProfile__header">
          <div className="agentProfile__photoWrap">
            {photo ? (
              <img src={photo} alt={broker.name} className="agentProfile__photo" />
            ) : (
              <div className="agentProfile__photoFallback">{initial}</div>
            )}
          </div>

          <div className="agentProfile__info">
            <span className="agentProfile__verifiedBadge">
              <IcCheck /> {t('brokerProfile.verified')}
            </span>
            <h1 className="agentProfile__name">{broker.name}</h1>
            {broker.agency && (
              <p className="agentProfile__agency">{broker.agency}</p>
            )}

            {isOwner && (
              <button className="agentProfile__editBtn" onClick={openEdit}>
                <IcEdit /> {t('brokerProfile.editProfile')}
              </button>
            )}
          </div>
        </header>

        {/* === STATS STRIP === */}
        <div className="agentProfile__statsStrip">
          <div className="agentProfile__stat">
            <IcHome />
            <span>{listingCount} active listing{listingCount === 1 ? '' : 's'}</span>
          </div>
          {broker.city && (
            <div className="agentProfile__stat">
              <IcPin />
              <span>{broker.city}, Cyprus</span>
            </div>
          )}
          {langs.length > 0 && (
            <div className="agentProfile__stat">
              <IcGlobe />
              <span>{langs.join(', ')}</span>
            </div>
          )}
        </div>

        {/* === ACTION BUTTONS === */}
        <div className="agentProfile__actions">
          {(broker.phone || broker.email) && (
            <button
              type="button"
              className="agentProfile__primaryAction"
              onClick={() => setShowContact(true)}
            >
              <IcPhone /> Get in Touch
            </button>
          )}
          <Link
            to={`/broker/${id}/listings`}
            className="agentProfile__secondaryAction"
          >
            <IcHome /> View All Listings →
          </Link>
        </div>

        {/* === DETAILS GRID === */}
        {(langs.length > 0 || broker.etek || bio || specialities.length > 0) && (
          <section className="agentProfile__details">
            {langs.length > 0 && (
              <div className="agentProfile__detailBlock">
                <span className="agentProfile__detailLabel">LANGUAGES</span>
                <div className="agentProfile__chipRow">
                  {langs.map((lang, idx) => (
                    <span key={idx} className="agentProfile__chip">{lang}</span>
                  ))}
                </div>
              </div>
            )}

            {broker.etek && (
              <div className="agentProfile__detailBlock">
                <span className="agentProfile__detailLabel">ETEK NO.</span>
                <p className="agentProfile__detailValue">{broker.etek}</p>
              </div>
            )}

            {specialities.length > 0 && (
              <div className="agentProfile__detailBlock">
                <span className="agentProfile__detailLabel">SPECIALITY</span>
                <div className="agentProfile__chipRow">
                  {specialities.map((s, idx) => (
                    <span key={idx} className="agentProfile__chip">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {bio && (
              <div className="agentProfile__detailBlock agentProfile__detailBlock--full">
                <span className="agentProfile__detailLabel">ABOUT</span>
                <p className="agentProfile__bio">{bio}</p>
              </div>
            )}
          </section>
        )}

        {/* === LISTINGS PREVIEW === */}
        <section className="agentProfile__listingsSection">
          <h2 className="agentProfile__sectionTitle">{t('brokerProfile.yourListings')}</h2>
          {listings.length === 0 ? (
            <p className="agentProfile__noListings">{t('brokerProfile.noListings')}</p>
          ) : (
            <div className="agentProfile__listingsGrid">
              {listings.map(l => (
                <Link key={l.id} to={`/property/${l.id}`} className="agentProfile__listingCard">
                  <div className="agentProfile__listingImg">
                    {l.images?.[0]
                      ? <img src={l.images[0]} alt={l.title} />
                      : <div className="agentProfile__listingImg-placeholder" />}
                  </div>
                  <div className="agentProfile__listingInfo">
                    <div className="agentProfile__listingPrice">
                      {fmt(l.price)}{l.price_label === 'for_rent' ? <span>/mo</span> : ''}
                    </div>
                    <div className="agentProfile__listingTitle">{l.title}</div>
                    <div className="agentProfile__listingMeta">
                      {l.city && <span><IcPin /> {l.city}</span>}
                      {l.beds  && <span>{l.beds} bed</span>}
                      {l.sqm   && <span>{l.sqm} m²</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* === REVIEWS === */}
        <section className="agentProfile__reviews">
          <h2 className="agentProfile__sectionTitle">Reviews</h2>
          <BrokerRatingSummary brokerId={id} />
          <ReviewsList brokerId={id} refreshTrigger={reviewRefresh} />
          <LeaveReviewForm
            brokerId={id}
            userId={user?.id}
            onReviewSubmitted={handleReviewSubmitted}
          />
        </section>
      </div>

      {/* ── Contact modal ── */}
      {showContact && (
        <div className="bpp-modal-overlay" onClick={() => setShowContact(false)}>
          <div className="bpp-modal bpp-modal--contact" onClick={e => e.stopPropagation()}>
            <div className="bpp-modal__header">
              <h2 className="bpp-modal__title">Get in Touch</h2>
              <button className="bpp-modal__close" onClick={() => setShowContact(false)}><IcClose /></button>
            </div>
            <div className="bpp-modal__body">
              <p className="bpp-contact-modal__name">{broker.name}</p>
              <div className="bpp-contact-modal__options">
                <div className="bpp-contact-modal__option">
                  <span className="bpp-contact-modal__icon"><IcPhone /></span>
                  <span>
                    <div className="bpp-contact-modal__label">Call</div>
                    <div className="bpp-contact-modal__value">
                      <MaskedPhone phone={broker.phone} />
                    </div>
                  </span>
                </div>
                {user && broker.email && (
                  <a href={`mailto:${broker.email}`} className="bpp-contact-modal__option">
                    <span className="bpp-contact-modal__icon"><IcMail /></span>
                    <span>
                      <div className="bpp-contact-modal__label">Email</div>
                      <div className="bpp-contact-modal__value">{broker.email}</div>
                    </span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit modal ── */}
      {showEdit && (
        <div className="bpp-modal-overlay" onClick={() => setShowEdit(false)}>
          <div className="bpp-modal" onClick={e => e.stopPropagation()}>
            <div className="bpp-modal__header">
              <h2 className="bpp-modal__title">{t('brokerProfile.editProfile')}</h2>
              <button className="bpp-modal__close" onClick={() => setShowEdit(false)}><IcClose /></button>
            </div>
            <div className="bpp-modal__body">
              <div className="bpp-modal__field">
                <label>Profile Photo</label>
                <BrokerAvatarUpload
                  currentAvatarUrl={broker.avatar_url}
                  userId={user?.id}
                  brokerId={broker.id}
                  onUploadComplete={(newUrl) => setBroker(b => ({ ...b, avatar_url: newUrl }))}
                />
              </div>
              <div className="bpp-modal__field">
                <label>{t('brokerProfile.editPhone')}</label>
                <input type="tel" value={editForm.phone} onChange={e => setEditForm(f => ({...f, phone: e.target.value}))} />
              </div>
              <div className="bpp-modal__field">
                <label>{t('brokerProfile.editSpeciality')}</label>
                <input type="text" placeholder={t('brokerProfile.editSpecPlaceholder')} value={editForm.speciality} onChange={e => setEditForm(f => ({...f, speciality: e.target.value}))} />
              </div>
              <div className="bpp-modal__field">
                <label>{t('brokerProfile.editLanguages')}</label>
                <input type="text" placeholder={t('brokerProfile.editLangPlaceholder')} value={editForm.languages} onChange={e => setEditForm(f => ({...f, languages: e.target.value}))} />
              </div>
              <div className="bpp-modal__field">
                <label>{t('brokerProfile.editDescription')}</label>
                <textarea rows={5} placeholder={t('brokerProfile.editDescPlaceholder')} value={editForm.description} onChange={e => setEditForm(f => ({...f, description: e.target.value}))} />
              </div>
              {saved && (
                <div className="bpp-modal__saved"><IcCheck /> {t('brokerProfile.saved')}</div>
              )}
            </div>
            <div className="bpp-modal__footer">
              <button className="bpp-modal__cancel" onClick={() => setShowEdit(false)}>{t('brokerProfile.cancelEdit')}</button>
              <button className="bpp-modal__submit" onClick={handleSave} disabled={saving}>
                {saving ? t('brokerProfile.saving') : t('brokerProfile.saveChanges')}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
